<template lang="pug">
#questionnaires-list(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .titles
            .title
              span アンケート
            .note
              span アンケートを作成し、家族・施設関係者に送付できます。
              br
              span 家族・施設関係者がLINEもしくはメールから回答し、施設側で回答結果を集計できます。
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.add-btn(@click="gotoQuestionnaireRegister" type="button") 新規作成
      .error-messages
        .error-message {{ errorMessage }}
    .box-body
      QuestionnaireTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onAcceptingAnswers="onAcceptingAnswers"
        @onDetail="onDetail"
        @onEdit="gotoQuestionnaireEdit"
        @onDelete="showDeleteModal"
        @onCopy="gotoQuestionnaireRegisterByCopy"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        //- 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・アンケートタイトル
        //- ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        //- ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )
  OkCancelModal(v-model="showAcceptEndModal" title="受付状態を「受付終了」に変更します。\n受付終了になるので回答期限は自動的に現在日時になります。\n変更してよろしいですか？"
    @ok="acceptEndQuestionnaire" @cancel="cancelAcceptEndQuestionnaire")
  AcceptStartModal(v-model="showAcceptStartModal" @ok="acceptStartQuestionnaire" @cancel="cancelAcceptStartQuestionnaire")

  OkCancelModal(v-model="showDeleteQuestionnaireModal" :title="deleteMessage" @ok="deleteQuestionnaire")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import QuestionnairesService, { QuestionnaireAnswersGetModel, QuestionnairesGetModel, QuestionnairesPatchModel, TargetType } from "@/commons/services/Questionnaires";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import QuestionnaireTable, { FilterSetting, QuestionnaireTableField, QuestionnaireTableItem } from "@/pc/containers/questionnaires/QuestionnaireTable.vue";
import AcceptStartModal from "@/pc/containers/questionnaires/AcceptStartModal.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### アンケート一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    QuestionnaireTable,
    AcceptStartModal
  }
})
export default class QuestionnairesList extends Vue {
  /** アンケートサービス */
  @Inject("QuestionnairesService") questionnairesService!: QuestionnairesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 受付終了モーダル表示可否 */
  showAcceptEndModal: boolean = false;
  /** 受付開始モーダル表示可否 */
  showAcceptStartModal: boolean = false;
  /** 削除確認モーダル表示可否 */
  showDeleteQuestionnaireModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** テーブル列名 */
  fields: Array<QuestionnaireTableField> = [];
  /** テーブル各行の情報 */
  items: Array<QuestionnaireTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** 受付状態変更対象のアンケート */
  acceptChangeTarget: QuestionnaireTableItem | null = null;
  /** 削除対象のアンケートID */
  deleteTargetId: number | null = null;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.questionnaires.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.questionnaires.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `questionnaire-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<QuestionnaireTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);

    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<QuestionnaireTableField> {
    let fields: Array<QuestionnaireTableField> = [];

    fields.push({ key: "status", label: "状態", sortable: false, sortDirection: "desc", class: "status text-center" });
    fields.push({ key: "terms", label: "アンケート期間", sortable: false, sortDirection: "desc", class: "terms text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "targets", label: "宛先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "responseRate", label: "回答率", sortable: false, sortDirection: "desc", class: "response-rate text-center" });
    fields.push({ key: "answersList", label: "回答結果", sortable: false, sortDirection: "desc", class: "answers-list text-center" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["questionnaire-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    await this.setRows();

    // テーブルに渡す各プロパティを作成する
    const questionnaires: Array<QuestionnairesGetModel> = await this.getQuestionnaires();
    const items: Array<QuestionnaireTableItem> = this.createItems(questionnaires);
    this.rows = this.items.length;
    this.items = items;

    // 現在ページがstoreに存在する場合は復元する
    if (this.$options.name && itemListFilterStore.currentPageMap[this.$options.name] != undefined) {
      this.currentPage = itemListFilterStore.currentPageMap[this.$options.name];
    }

    // 回答率は非同期で設定する
    const currentPage: number = this.currentPage;
    const perPage: number = this.perPage;
    this.setResponseCountByCurrentPage(this.items, currentPage, perPage);

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.questionnairesService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### アンケートを取得する
   */
  public async getQuestionnaires(): Promise<Array<QuestionnairesGetModel>> {
    let response: Array<QuestionnairesGetModel> = [];

    try {
      response = await this.questionnairesService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.questionnairesService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.questionnairesService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(questionnaires: Array<QuestionnairesGetModel>): Array<QuestionnaireTableItem> {
    let items: Array<QuestionnaireTableItem> = [];

    for (const questionnaire of questionnaires) {
      // 受付期限を過ぎている場合は受付終了として扱う
      if (questionnaire.acceptingAnswers) {
        if (this.isOverLimit(questionnaire)) {
          questionnaire.acceptingAnswers = false;
        }
      }

      items.push({
        ...questionnaire,
        responseCount: null,
        targetsCount: null
      });
    }

    return items;
  }

  /**
   * ### 期限が過ぎているかを返す
   *
   * - 期限が設定されていない場合は期限が過ぎていないものとして扱う
   */
  isOverLimit(questionnaire: QuestionnairesGetModel): boolean {
    if (questionnaire.replyLimit == null) {
      return false;
    }
    const now: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
    if (questionnaire.replyLimit < now) {
      return true;
    }
    return false;
  }

  /**
   * ### 「回答率」列の情報を取得する
   */
  public async setResponseCountByCurrentPage(items: Array<QuestionnaireTableItem>, currentPage: number, perPage: number): Promise<void> {
    const startIndex: number = (currentPage - 1) * perPage; // 現在ページの開始インデックス
    const endIndex: number = Math.min(startIndex + perPage, items.length); // 現在ページの終了インデックス

    let requestPromises: Array<Promise<void>> = [];
    for (let i = startIndex; i < endIndex; i++) {
      if (items[i].responseCount == null || items[i].targetsCount == null) {
        requestPromises.push(this.setResponseCount(items[i]));
      }
    }
    let results: Array<any> = await Promise.all(requestPromises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }
    return;
  }

  /**
   * ### 回答率の情報を設定する
   */
  public async setResponseCount(item: QuestionnaireTableItem): Promise<void> {
    try {
      const id: number = item.id;
      const answers: Array<QuestionnaireAnswersGetModel> = await this.questionnairesService.getAnswers(userStore, id);
      item.responseCount = answers.length;
      item.targetsCount = item.targets.length;
    } catch (error) {
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;

    // 回答率は非同期で設定する
    const currentPage: number = this.currentPage;
    this.setResponseCountByCurrentPage(this.items, currentPage, perPage);

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 回答率は非同期で設定する
    const perPage: number = this.perPage;
    this.setResponseCountByCurrentPage(this.items, page, perPage);

    return;
  }

  public onAcceptingAnswers(item: QuestionnaireTableItem): void {
    this.acceptChangeTarget = item;
    if (item.acceptingAnswers == false) {
      this.showAcceptEndModal = true;
    } else {
      this.showAcceptStartModal = true;
    }
    return;
  }

  public async acceptEndQuestionnaire(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const item: QuestionnaireTableItem = this.acceptChangeTarget as QuestionnaireTableItem;
      const replyLimit: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
      const requestBody: QuestionnairesPatchModel = this.createQuestionnairesPatchModel(item, replyLimit);
      const result = await this.questionnairesService.patch(userStore, item.id, requestBody);
      // updatedを差し替えて連続更新しても409が発生しないようにし、再読み込みをしない
      item.updated = result.updated;
      item.replyLimit = replyLimit;

      this.errorMessage = "";
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public createQuestionnairesPatchModel(item: QuestionnaireTableItem, replyLimit: number | null): QuestionnairesPatchModel {
    return {
      title: item.title ? item.title : "",
      text: item.text ? item.text : "",
      fileUrls: item.fileUrls ? item.fileUrls : [],
      hidden: item.hidden,
      messageTitle: item.messageTitle ? item.messageTitle : "",
      messageText: item.messageText ? item.messageText : "",
      targets: item.targets,
      lineTargets: [],
      mailTargets: [],
      sendMessageGroupInfos: item.sendMessageGroupInfos ? item.sendMessageGroupInfos : [],
      residentGroupInfos: item.residentGroupInfos ? item.residentGroupInfos : [],
      otherUserGroupInfos: item.otherUserGroupInfos ? item.otherUserGroupInfos : [],
      targetType: item.targetType as any,
      questions: item.questions,
      replyLimit: replyLimit ? replyLimit : null,
      needSendMessage: false, // メッセージ送信はしない
      acceptingAnswers: item.acceptingAnswers,
      shareStandardUsers: item.shareStandardUsers,
      updated: item.updated
    };
  }

  public cancelAcceptEndQuestionnaire(): void {
    const item: QuestionnaireTableItem = this.acceptChangeTarget as QuestionnaireTableItem;
    item.acceptingAnswers = !item.acceptingAnswers;
    return;
  }

  /**
   * ### acceptingAnswersの変更通知
   *
   * - 引数のitemのacceptingAnswersは変更済みの値が格納されている
   */
  public async acceptStartQuestionnaire(replyLimit: number | null): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const item: QuestionnaireTableItem = this.acceptChangeTarget as QuestionnaireTableItem;
      const requestBody: QuestionnairesPatchModel = this.createQuestionnairesPatchModel(item, replyLimit);
      const result = await this.questionnairesService.patch(userStore, item.id, requestBody);
      // updatedを差し替えて連続更新しても409が発生しないようにし、再読み込みをしない
      item.updated = result.updated;
      item.replyLimit = replyLimit as number;

      // 更新後の期限が過去に設定された場合、受付状態は受付終了として扱う
      if (this.isOverLimit(item)) {
        item.acceptingAnswers = false;
      }

      this.errorMessage = "";
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public cancelAcceptStartQuestionnaire(): void {
    const item: QuestionnaireTableItem = this.acceptChangeTarget as QuestionnaireTableItem;
    item.acceptingAnswers = !item.acceptingAnswers;
    return;
  }

  /**
   * ### 回答結果を表示する
   */
  public async onDetail(item: QuestionnaireTableItem): Promise<void> {
    this.$router.push({ name: PcUrl.QuestionnaireDetail, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### アンケート登録画面へ遷移する
   */
  public gotoQuestionnaireRegister(): void {
    this.$router.push(PcUrl.QuestionnaireRegister);
    return;
  }

  /**
   * ### アンケート編集画面へ遷移
   */
  public gotoQuestionnaireEdit(item: QuestionnaireTableItem): void {
    this.$router.push({ name: PcUrl.QuestionnaireEdit, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public showDeleteModal(item: QuestionnaireTableItem): void {
    this.deleteTargetId = item.id;
    const title: string = item.title ? item.title : "タイトル無し";
    this.deleteMessage = `${title}を削除しますか？`;
    this.showDeleteQuestionnaireModal = true;
    return;
  }

  /**
   * ### アンケートを削除する
   *
   * - アンケート削除をサービス実行
   * - 削除成功ダイアログを表示する
   * - 削除したアンケートを差し引く
   */
  public async deleteQuestionnaire(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.questionnairesService.delete(userStore, this.deleteTargetId as number);
    this.showDeleteSuccessModal = true;

    // 削除した情報を更新するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: QuestionnaireTableItem) => {
      if (item.id == this.deleteTargetId) {
        return false;
      } else {
        return true;
      }
    });

    this.isBusy = false;

    return;
  }

  public gotoQuestionnaireRegisterByCopy(item: QuestionnaireTableItem): void {
    this.$router.push(PcUrl.QuestionnaireRegister + `?id=${item.id}`);
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setCurrentPage({ url: this.$options.name, currentPage: this.currentPage });
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
#questionnaires-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;

    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
